import { RowInput } from "jspdf-autotable";
import { PoolInfo, PoolMaterial } from "../../../context/types";
import { Measures } from "../../../types/context/poolCalculations";
import { PoolShape } from "../../../constants/pool";
import { formatNumber } from "../../../utils/formatVolume";

const getPoolDimensionsString = (
  poolShape: PoolShape,
  poolInfo: PoolInfo
): RowInput[] => {
  switch (poolShape) {
    case PoolShape.RECTANGULAR:
      return [
        [
          {
            content: `Pool Dimensions`,
            styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
          },
          {
            content: `Length ${poolInfo.length}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
          {
            content: `Width ${poolInfo.width}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
          {
            content: `Depth ${poolInfo.depth}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
        ],
      ];

    case PoolShape.ROUND:
      return [
        [
          {
            content: `Pool Dimensions:`,
            styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
          },
          {
            content: `Diameter ${poolInfo.diameter}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
          {
            content: `Depth ${poolInfo.depth}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
        ],
      ];
    case PoolShape.SQUARE:
      return [
        [
          {
            content: `Pool Dimensions:`,
            styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
          },
          {
            content: `Length ${poolInfo.length}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
          {
            content: `Width ${poolInfo.length}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
          {
            content: `Depth ${poolInfo.depth}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
        ],
      ];
    case PoolShape.KIDNEY:
      return [
        [
          {
            content: `Pool Dimensions:`,
            styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
          },
          {
            content: `Surface Area ${poolInfo.surfaceArea}m²`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
          {
            content: `Depth ${poolInfo.depth}m`,
            styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
          },
        ],
      ];

    default:
      return [];
  }
};

export const getPoolInfoTableData = (
  poolInfo: PoolInfo,
  measures: Measures
) => {
  const header = [
    {
      content: "Pool Information",
      styles: {
        fontStyle: "normal" as const,
        font: "NotoSans-Regular",
      },
    },
    "",
    "",
    "",
  ];

  
  
  
  const volumeDisclaimer =
    poolInfo.poolMaterial === PoolMaterial.FIBERGLASS ||
    poolInfo.poolMaterial === PoolMaterial.PLASTICLINER
      ? ` (Because the pool material is ${poolInfo.poolMaterial}, the volume was adjusted.)`
      : "";

  const rows: RowInput[] = [
    [
      {
        content: `Pool Location`,
        styles: {
          fontStyle: "normal" as const,
          font: "NotoSans-Regular",
        },
      },
      {
        content: `${poolInfo.location}`,
        styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
      },
    ],
    [
      {
        content: `With Cover`,
        styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
      },
      {
        content: `${poolInfo.withCover ? "Yes" : "No"}`,
        styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
      },
    ],
    [
      {
        content: `Pool Material`,
        styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
      },
      {
        content: `${poolInfo.poolMaterial}`,
        styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
      },
    ],
    [
      {
        content: `Required Temperature`,
        styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
      },
      {
        content: `${poolInfo.requirePoolTemp}°C`,
        styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
      },
    ],
    [
      {
        content: `Operation Hours`,
        styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
      },
      {
        content: `${poolInfo.operatingHours} hrs`,
        styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
      },
    ],
    [
      {
        content: `Electricity Cost`,
        styles: { fontStyle: "normal" as const, font: "NotoSans-Regular" },
      },
      {
        content: `$ ${poolInfo.electricityCost}`,
        styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
      },
    ],
    [
      {
        content: `Estimated Pool Volume`,
        styles: {
          fontStyle: "normal" as const,
          font: "NotoSans-Regular",
        },
      },
      {
        content: `${formatNumber(
          measures.estimateVol
        )} Litres${volumeDisclaimer}`,
        styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
      },
    ],
    [
      {
        content: `Pool Shape`,
        styles: {
          fontStyle: "normal" as const,
          font: "NotoSans-Regular",
          minCellHeight: 25,
        },
      },
      {
        content: `${poolInfo.shape}`,
        styles: { fontStyle: "bold" as const, font: "NotoSans-Bold" },
      },
    ],
  ];

  const poolDimensions = getPoolDimensionsString(poolInfo.shape, poolInfo);

  return { header, rows: [...rows, ...poolDimensions] };
};
